// ---------------------------------------------------------------------------
// CandidatePreviewIsland — full-size block preview + keyboard navigation.
//
// Registered as `studio.brainstorm.candidate-preview` in the host IslandRegistry.
// Reads `candidates`, `collapse`, `apply`, `selectNext`, `selectPrev` from
// the brainstorm slice. Keyboard nav: ArrowRight → next, ArrowLeft → prev,
// Escape → collapse.
//
// v1.0.9 — when the Candidate carries a Rewriter template (i.e. the real
// provider sourced it from examples-v1.ttl), the underlying cogoJson.ui
// already IS the template. The picker renders slot expressions as literal
// strings because no client-side binder runs —
// feedback_brainstorm_binder_defer_2026_04_19.md gates binding to the WASM
// binder. We surface the `promptHint` one-liner above the BlockV2 preview
// so reviewers know at a glance what bind-keys / event-keys the template
// expects.
// ---------------------------------------------------------------------------

import { useEffect } from 'react'
import { BlockV2 } from '@cogo/react-renderer-v2'
import { useStudioStore } from '@/store/studio-store'
import type { BlockDef } from '@cogo/types-v2'

interface CandidatePreviewIslandProps {
  expandedId?: string
}

export function CandidatePreviewIsland({ expandedId = '' }: CandidatePreviewIslandProps) {
  const candidate = useStudioStore((s) =>
    s.brainstorm.candidates.find((c) => c.id === expandedId) ?? null,
  )
  const collapse = useStudioStore((s) => s.brainstorm.collapse)
  const apply = useStudioStore((s) => s.brainstorm.apply)
  const selectNext = useStudioStore((s) => s.brainstorm.selectNext)
  const selectPrev = useStudioStore((s) => s.brainstorm.selectPrev)

  useEffect(() => {
    if (!expandedId) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') collapse()
      else if (e.key === 'ArrowRight') selectNext()
      else if (e.key === 'ArrowLeft') selectPrev()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [expandedId, collapse, selectNext, selectPrev])

  if (!expandedId || !candidate) return null

  // v1.0.9 hint strip — only renders when the candidate came from a real
  // v1.0.9 shard row AND the Rewriter populated template / promptHint.
  // Mock fixtures leave both undefined so the strip is hidden on dev paths.
  const hasTemplate = candidate.template !== undefined
  const promptHint = candidate.promptHint

  return (
    <div className="flex flex-col h-full">
      {(hasTemplate || promptHint) && (
        <div
          data-testid="brainstorm-preview-v109-strip"
          className="shrink-0 px-4 py-2 border-b border-border bg-foreground/[0.02] flex items-center gap-3 text-[11px]"
        >
          {hasTemplate && (
            <span
              data-testid="brainstorm-preview-template-badge"
              className="shrink-0 px-1.5 py-0.5 rounded border border-primary/30 bg-primary/10 text-primary font-medium"
              title="Candidate body is a v1.0.9 Rewriter template — slot expressions render verbatim until the WASM binder runs on apply."
            >
              template
            </span>
          )}
          {promptHint && (
            <code
              data-testid="brainstorm-preview-prompt-hint"
              className="font-mono text-foreground/80 truncate"
              title={promptHint}
            >
              {promptHint}
            </code>
          )}
        </div>
      )}
      <div className="flex-1 overflow-auto p-6">
        <BlockV2 block={candidate.cogoJson as BlockDef} />
      </div>
      <div className="border-t border-border p-3 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">{candidate.exampleId}</div>
          <div className="text-sm font-medium">{candidate.narrativeHeadline}</div>
        </div>
        <button
          type="button"
          data-testid="brainstorm-apply"
          onClick={() => apply()}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Use this
        </button>
      </div>
    </div>
  )
}
